/* ─── Shop Engine : pool individuel, RNG pondérée, draft quotidien ───────────
   Architecture stricte (cf. cahier des charges) :

   1. POOL INDIVIDUEL — la boutique génère SHOP_SIZE cartes par jour ET par
      joueur. L'état est propre à chaque `userId` : ce que le joueur A achète
      ne disparaît jamais pour le joueur B. Aucun stock global partagé.

   2. RARETÉ PONDÉRÉE — la RNG du shop tire d'abord un Tier selon TIER_WEIGHTS
      (60 / 30 / 10) puis une carte au hasard dans ce tier.

   3. VERROUILLAGE AVANT 16H — le joueur possède un Inventaire (cartes achetées)
      et envoie UN atout sur le terrain d'équipe partagé (`active`, longueur ≤ 1).
      Les combos se forment entre les atouts de même famille de toute l'équipe.
      Au passage d'un nouveau jour, l'atout envoyé est retiré SAUF s'il a été
      verrouillé (`locked`).                                                    */

import {
  CARDS,
  CARD_BY_ID,
  CardDef,
  CardFamily,
  CardTier,
  FAMILIES,
  FAMILY_LIST,
  SHOP_SIZE,
  TIER_WEIGHTS,
} from "../constants/cards";

/* ─── Interfaces d'état ────────────────────────────────────────────────────── */

/** Boutique personnelle d'un joueur pour une journée donnée. */
export interface ShopState {
  date: string; // jour de génération, format YYYY-MM-DD
  offers: string[]; // ids des SHOP_SIZE cartes proposées
  rerollsUsed: number; // nombre de rerolls effectués aujourd'hui
}

/** Inventaire = cartes achetées (id → quantité possédée). */
export type PlayerInventory = Record<string, number>;

/** Slots actifs = ids des cartes rendues actives pour le match du jour. */
export type ActiveSlots = string[];

/** État complet du système de cartes, stocké sur le Player (`league_cards`). */
export interface LeagueCardState {
  inventory: PlayerInventory;
  active: ActiveSlots;
  locked: boolean; // empêche le reset des slots actifs au changement de jour
  shop: ShopState;
}

/* ─── RNG déterministe (mulberry32 + hash FNV-1a) ──────────────────────────── */

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ─── Génération du shop pondéré ───────────────────────────────────────────── */

const TOTAL_WEIGHT =
  TIER_WEIGHTS[1] + TIER_WEIGHTS[2] + TIER_WEIGHTS[3] + TIER_WEIGHTS[4];

/** Tire un Tier (1/2/3/4) selon les poids de rareté. */
export function pickTier(rng: () => number): CardTier {
  const r = rng() * TOTAL_WEIGHT;
  if (r < TIER_WEIGHTS[1]) return 1;
  if (r < TIER_WEIGHTS[1] + TIER_WEIGHTS[2]) return 2;
  if (r < TIER_WEIGHTS[1] + TIER_WEIGHTS[2] + TIER_WEIGHTS[3]) return 3;
  return 4;
}

const CARDS_BY_TIER: Record<CardTier, CardDef[]> = {
  1: CARDS.filter((c) => c.tier === 1),
  2: CARDS.filter((c) => c.tier === 2),
  3: CARDS.filter((c) => c.tier === 3),
  4: CARDS.filter((c) => c.tier === 4),
};

/**
 * Génère SHOP_SIZE cartes distinctes via la RNG pondérée fournie.
 * `rng` peut être déterministe (génération quotidienne) ou Math.random
 * enveloppé (reroll).
 */
export function rollShop(rng: () => number): string[] {
  const offers: string[] = [];
  let guard = 0;
  while (offers.length < SHOP_SIZE && guard++ < 80) {
    const tier = pickTier(rng);
    const pool = CARDS_BY_TIER[tier];
    const card = pool[Math.floor(rng() * pool.length)];
    if (card && !offers.includes(card.id)) offers.push(card.id);
  }
  // Filet de sécurité si la RNG n'a pas rempli (improbable)
  if (offers.length < SHOP_SIZE) {
    for (const c of CARDS) {
      if (offers.length >= SHOP_SIZE) break;
      if (!offers.includes(c.id)) offers.push(c.id);
    }
  }
  return offers;
}

/** Clé de jour locale (YYYY-MM-DD). */
export function todayKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Shop déterministe stable pour un (joueur, jour) donné. */
export function dailyShop(userKey: string, dayKey: string): ShopState {
  const rng = mulberry32(hashStr(`${userKey}::${dayKey}`));
  return { date: dayKey, offers: rollShop(rng), rerollsUsed: 0 };
}

/* ─── État initial / migration ─────────────────────────────────────────────── */

export function initCardState(
  userKey: string,
  now: Date = new Date()
): LeagueCardState {
  return {
    inventory: {},
    active: [],
    locked: false,
    shop: dailyShop(userKey, todayKey(now)),
  };
}

/**
 * Garantit que le shop correspond au jour courant.
 * Si `locked` est vrai, la rotation est gelée : on ne touche à rien (les offres
 * ET l'atout envoyé sont conservés jusqu'au déverrouillage).
 * Sinon, au changement de jour : le shop est régénéré (rerolls remis à 0) et
 * l'atout envoyé est retiré.
 * Renvoie le même objet si rien ne change (pour éviter les re-renders inutiles).
 */
export function ensureDailyShop(
  state: LeagueCardState,
  userKey: string,
  now: Date = new Date()
): LeagueCardState {
  const today = todayKey(now);
  if (state.shop && state.shop.date === today) return state;
  if (state.locked) return state; // rotation gelée tant que verrouillé
  return {
    ...state,
    active: [],
    shop: dailyShop(userKey, today),
  };
}

/* ─── Combos / synergies ───────────────────────────────────────────────────── */

export interface ComboActivation {
  family: CardFamily;
  label: string;
  comboName: string;
  count: number;
  color: string;
  icon: string;
  /** Paliers atteints (2 et/ou 4 cartes actives de la famille). */
  tier2: boolean;
  tier4: boolean;
  effects: string[];
}

/**
 * Nombre de cartes par famille parmi les ids fournis. Une carte multi-familles
 * compte pour CHACUNE de ses familles (mécanique de combo type TFT).
 */
export function familyCounts(cardIds: string[]): Record<CardFamily, number> {
  const counts: Record<CardFamily, number> = {
    tribune: 0,
    staff: 0,
    environnement: 0,
    vip: 0,
  };
  for (const id of cardIds) {
    const c = CARD_BY_ID[id];
    if (c) for (const fam of c.families) counts[fam]++;
  }
  return counts;
}

/** Combos actuellement activés par les cartes fournies (terrain d'équipe). */
export function getActiveCombos(activeIds: string[]): ComboActivation[] {
  const counts = familyCounts(activeIds);
  return FAMILY_LIST.map((fam) => {
    const meta = FAMILIES[fam];
    const count = counts[fam];
    const tier2 = count >= 2;
    const tier4 = count >= 4;
    const effects: string[] = [];
    if (tier2) effects.push(meta.combo2);
    if (tier4) effects.push(meta.combo4);
    return {
      family: fam,
      label: meta.label,
      comboName: meta.comboName,
      count,
      color: meta.color,
      icon: meta.icon,
      tier2,
      tier4,
      effects,
    };
  }).filter((c) => c.count > 0);
}

/* ─── Modificateurs de match (lus par le moteur à 16h) ──────────────────────── */

export interface CardModifiers {
  atkMult: number; // multiplicateur d'attaque de mon équipe
  defMult: number; // multiplicateur de défense de mon équipe
  enemyDefMult: number; // multiplicateur de défense adverse
  buildUpMult: number; // arrivée dans la surface
  goldMult: number; // multiplicateur sur les gains de Golds (Célébrité)
  flatGold: number; // Golds fixes après match (Sponsor Qatari)
  gkLevelDelta: number; // variation de niveau du gardien (Président)
}

/**
 * Agrège les effets des cartes actives + bonus de combo en modificateurs
 * consommables par `simulateMatch`. v1 : effets globaux principaux ; les
 * effets situationnels (annulation de but, bouclier…) seront branchés
 * ultérieurement dans le déroulé minute par minute.
 */
export function computeCardModifiers(activeIds: string[]): CardModifiers {
  const mods: CardModifiers = {
    atkMult: 1,
    defMult: 1,
    enemyDefMult: 1,
    buildUpMult: 1,
    goldMult: 1,
    flatGold: 0,
    gkLevelDelta: 0,
  };

  const has = (id: string) => activeIds.includes(id);

  // Tribune
  if (has("fanatique")) mods.atkMult *= 1.05;
  if (has("tambour")) mods.buildUpMult *= 1.1;
  if (has("kop_nord")) mods.enemyDefMult *= 0.95;

  // Environnement (effet attaque globale Canicule, build-up Pelouse)
  if (has("canicule")) mods.atkMult *= 0.85;
  if (has("pelouse_gelee")) mods.buildUpMult *= 0.8;

  // VIP
  if (has("sponsor_qatari")) mods.flatGold += 2;
  if (has("celebrite_loge")) mods.goldMult *= 2; // appliqué seulement si victoire côté appelant
  if (has("president_colerique")) {
    mods.atkMult *= 1.15;
    mods.defMult *= 1.15;
    mods.gkLevelDelta -= 1;
  }

  // Combos
  const combos = getActiveCombos(activeIds);
  for (const c of combos) {
    if (c.family === "staff" && c.tier2) mods.defMult *= 1.12;
    if (c.family === "tribune" && c.tier4) mods.enemyDefMult *= 0.85;
  }

  return mods;
}
